import BDImage from '@shared/components/elements/bd-image';
import clsx from 'clsx';
import { StaticImageData } from 'next/image';
import { ReactNode } from 'react';

export default function Section({ children }: { children: ReactNode }) {
  return (
    <section className="mx-auto w-full px-4 pt-4 sm:px-6 lg:px-8 xl:px-10">{children}</section>
  );
}

export function HeroSection({
  title,
  image,
  children,
}: {
  title: string;
  image: StaticImageData;
  children?: ReactNode;
}) {
  return (
    <section className="aspect-4/3 md:aspect-16/9 relative -mt-32 w-full">
      <BDImage src={image} alt={title} className="h-full w-full object-cover" />
      <div className="absolute top-[calc(50%+2em)] grid w-full -translate-y-1/2 grid-cols-2 sm:px-6 md:px-10 xl:px-20">
        <div className="text-bd-primary pl-6">
          <h1 className="xs:text-xl text-lg sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl">
            {title}
          </h1>
          <div className="mt-2 hidden md:mt-6 md:block xl:mt-12">{children}</div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[10%] max-h-12 bg-gradient-to-b from-transparent via-transparent to-white" />
      {/* <DynamicDownArrow /> */}
    </section>
  );
}

export function SpanPageContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={clsx(
        'xl:mx-22 flex max-w-screen-xl flex-col gap-y-0 md:mx-10 lg:mx-16',
        className
      )}
    >
      {children}
    </section>
  );
}

export function InnerSpanPageContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={clsx(
        'xs:mx-4 xs:my-4 mx-2 my-2 flex flex-col sm:mx-6 lg:my-8 lg:gap-y-10',
        className
      )}
    >
      {children}
    </section>
  );
}

export function PageContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={clsx(
        'xs:mx-4 xs:my-4 xl:mx-22 mx-2 my-2 flex max-w-screen-xl flex-col gap-y-0 sm:mx-6 md:mx-10 lg:mx-16 lg:my-8 lg:gap-y-20',
        className
      )}
    >
      {children}
    </section>
  );
}

export function SpanSection({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={clsx('gap-y-6 p-4 md:p-6 lg:p-8 xl:p-12', className)}>
      {children}
    </section>
  );
}

export function HSection({
  children,
  className,
  id,
  bgClassName,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  bgClassName?: string;
}) {
  return (
    <section
      id={id}
      className={clsx(
        'relative flex flex-col gap-6 pt-8 lg:pt-12',
        bgClassName
          ? '-mx-4 mt-8 p-4 sm:-mx-6 sm:mt-0 sm:p-6 md:rounded-xl lg:-mx-10 lg:p-10 xl:-mx-16 xl:p-16'
          : '',
        bgClassName,
        className
      )}
    >
      {children}
    </section>
  );
}

export function RightSection({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={clsx('flex flex-col gap-y-8 px-6 md:gap-y-16 md:px-12', className)}>
      {children}
    </section>
  );
}
